import React, { useState, useEffect, useRef } from 'react';
import { claimSpinnerReward, checkSpinnerCooldown } from '../../api/PlayerDataApi';

const SpinnerReward = () => {
  const [isSpinning, setIsSpinning] = useState(false);
  const [reward, setReward] = useState(null);
  const [error, setError] = useState(null);
  const [cooldown, setCooldown] = useState({
    active: false,
    timeLeft: 0,
    expiresAt: null
  });

  const spinnerRef = useRef(null);
  const cooldownInterval = useRef(null);
  const confettiContainerRef = useRef(null);

  // Check cooldown on component mount
  useEffect(() => {
    const cooldownStatus = checkSpinnerCooldown();
    setCooldown(cooldownStatus);

    if (cooldownStatus.active && cooldownStatus.timeLeft > 0) {
      startCooldownTimer(cooldownStatus.timeLeft);
    }

    return () => {
      if (cooldownInterval.current) {
        clearInterval(cooldownInterval.current);
      }
    };
  }, []);

  // Start countdown timer for cooldown
  const startCooldownTimer = (timeLeft) => {
    if (cooldownInterval.current) {
      clearInterval(cooldownInterval.current);
    }

    cooldownInterval.current = setInterval(() => {
      setCooldown(prev => {
        const newTimeLeft = prev.timeLeft - 1;
        if (newTimeLeft <= 0) {
          clearInterval(cooldownInterval.current);
          return { active: false, timeLeft: 0 };
        }
        return { ...prev, active: true, timeLeft: newTimeLeft };
      });
    }, 1000);
  };

  // Format time display for cooldown
  const formatTime = (seconds) => {
    if (seconds >= 3600) {
      const hours = Math.floor(seconds / 3600);
      const mins = Math.floor((seconds % 3600) / 60);
      return `${hours}h ${mins}m`;
    } else {
      const min = Math.floor(seconds / 60);
      const sec = seconds % 60;
      return `${min}:${sec < 10 ? '0' : ''}${sec}`;
    }
  };

  // Handle the spinner animation and reward claim
  const handleSpinClick = async () => {
    if (isSpinning || cooldown.active) return;

    try {
      setIsSpinning(true);
      setError(null);
      setReward(null);

      // Rotate the spinner with CSS animation
      if (spinnerRef.current) {
        spinnerRef.current.style.transition = 'transform 4s cubic-bezier(0.23, 1, 0.32, 1)';
        const randomRotation = 2160 + Math.random() * 360; // At least 6 full rotations
        spinnerRef.current.style.transform = `rotate(${randomRotation}deg)`;
      }

      // Wait for animation to complete before showing reward
      setTimeout(async () => {
        try {
          const response = await claimSpinnerReward();

          // Update to match the new response format
          setReward({
            coins: response.reward.coins,
            message: response.reward.message,
            newBalance: response.new_coin_balance
          });

          // Update user data in localStorage to reflect new coin balance
          const playerData = JSON.parse(localStorage.getItem("player_data") || "{}");
          playerData.coin_balance = response.new_coin_balance;
          localStorage.setItem("player_data", JSON.stringify(playerData));

          // Create enhanced confetti effect
          createConfetti();

        } catch (err) {
          setError(err.message || "Failed to claim reward. Please try again later.");
        } finally {
          setIsSpinning(false);
        }
      }, 4000);
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again later.");
      setIsSpinning(false);
    }
  };

  // Create enhanced confetti effect when reward is received
  const createConfetti = () => {
    if (!confettiContainerRef.current) return;

    const container = confettiContainerRef.current;
    container.innerHTML = '';

    // Create more confetti pieces with better effects
    for (let i = 0; i < 150; i++) {
      const confetti = document.createElement('div');
      confetti.classList.add('confetti');
      const size = Math.random() * 12 + 6;

      confetti.style.width = `${size}px`;
      confetti.style.height = `${size}px`;
      confetti.style.background = getRandomColor();
      confetti.style.left = `${Math.random() * 100}%`;
      confetti.style.top = `${Math.random() * 100}%`;
      confetti.style.transform = `rotate(${Math.random() * 360}deg)`;
      confetti.style.borderRadius = Math.random() > 0.5 ? '50%' : '2px';

      // Enhanced animation settings
      confetti.style.position = 'absolute';
      confetti.style.animation = `confetti-fall ${Math.random() * 3 + 3}s ease-out forwards`;
      confetti.style.boxShadow = '0 2px 4px rgba(0,0,0,0.2)';

      container.appendChild(confetti);
    }

    // Remove confetti after animation finishes
    setTimeout(() => {
      if (confettiContainerRef.current) {
        confettiContainerRef.current.innerHTML = '';
      }
    }, 6000);
  };

  // Get random vibrant color for confetti
  const getRandomColor = () => {
    const colors = [
      '#FFD700', '#FF6B35', '#4CAF50', '#2196F3', '#9C27B0', '#F44336',
      '#FF9800', '#E91E63', '#3F51B5', '#00BCD4', '#8BC34A', '#FFC107'
    ];
    return colors[Math.floor(Math.random() * colors.length)];
  };

  return (
    <div className="space-y-8">
      {/* Header with gradient */}
      <div className="text-center">
        <h2 className="text-3xl font-black bg-gradient-to-r from-purple-600 via-pink-600 to-yellow-500 bg-clip-text text-transparent mb-2">
          🎰 Lucky Spinner
        </h2>
        <p className="text-gray-600">Spin the wheel of fortune and win amazing rewards!</p>
      </div>

      {/* Main Spinner Container */}
      <div className="relative">
        {/* Background glow effect */}
        <div className="absolute inset-0 bg-gradient-to-r from-purple-400/20 via-pink-400/20 to-yellow-400/20 rounded-3xl blur-3xl transform scale-110"></div>

        <div className="relative bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl overflow-hidden border border-white/20">
          <div className="p-8 flex flex-col items-center justify-center">
            {/* Confetti Container */}
            <div
              ref={confettiContainerRef}
              className="absolute inset-0 pointer-events-none overflow-hidden rounded-3xl"
              style={{ zIndex: 10 }}
            ></div>

            {/* Enhanced Spinner Wheel */}
            <div className="relative w-80 h-80 mb-8">
              {/* Outer glow ring */}
              <div className="absolute inset-0 rounded-full bg-gradient-to-r from-purple-500 via-pink-500 to-yellow-500 p-2 animate-pulse">
                <div className="w-full h-full rounded-full bg-white"></div>
              </div>

              {/* Main Spinner Wheel */}
              <div
                ref={spinnerRef}
                className="relative w-full h-full rounded-full flex items-center justify-center shadow-2xl overflow-hidden"
                style={{
                  background: 'conic-gradient(from 0deg, #FFD700, #FFA500, #FF6347, #FF1493, #8A2BE2, #4169E1, #00CED1, #32CD32, #FFD700)',
                  boxShadow: '0 0 40px rgba(255, 215, 0, 0.4), inset 0 0 40px rgba(255, 255, 255, 0.1)'
                }}
              >
                {/* Inner segments with prizes */}
                <div className="absolute inset-4 rounded-full overflow-hidden">
                  {[...Array(12)].map((_, i) => {
                    const prizes = ['5', '10', '25', '50', '100', '15', '30', '75', '5', '20', '40', '200'];
                    const colors = [
                      'bg-gradient-to-br from-yellow-300 to-yellow-500',
                      'bg-gradient-to-br from-orange-300 to-orange-500',
                      'bg-gradient-to-br from-red-300 to-red-500',
                      'bg-gradient-to-br from-pink-300 to-pink-500',
                      'bg-gradient-to-br from-purple-300 to-purple-500',
                      'bg-gradient-to-br from-blue-300 to-blue-500',
                      'bg-gradient-to-br from-cyan-300 to-cyan-500',
                      'bg-gradient-to-br from-green-300 to-green-500',
                      'bg-gradient-to-br from-lime-300 to-lime-500',
                      'bg-gradient-to-br from-amber-300 to-amber-500',
                      'bg-gradient-to-br from-rose-300 to-rose-500',
                      'bg-gradient-to-br from-indigo-300 to-indigo-500'
                    ];

                    return (
                      <div
                        key={i}
                        className={`absolute top-0 left-1/2 w-1/2 h-1/2 origin-bottom-left ${colors[i]} flex items-center justify-center`}
                        style={{
                          transform: `rotate(${i * 30}deg)`,
                          clipPath: 'polygon(0 0, 100% 0, 50% 100%)'
                        }}
                      >
                        <div
                          className="text-white font-black text-sm transform -rotate-15 drop-shadow-lg"
                          style={{
                            textShadow: '0 2px 4px rgba(0,0,0,0.5)',
                            writingMode: 'vertical-rl',
                            textOrientation: 'mixed'
                          }}
                        >
                          {prizes[i]}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Center hub with coin icon */}
                <div className="relative z-10 w-24 h-24 rounded-full bg-gradient-to-br from-yellow-300 via-yellow-400 to-yellow-500 flex items-center justify-center shadow-inner border-4 border-white">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-t from-yellow-200 to-white flex items-center justify-center shadow-lg">
                    <svg className="w-10 h-10 text-yellow-600 drop-shadow-md animate-pulse" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z"></path>
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd"></path>
                    </svg>
                  </div>
                </div>
              </div>

              {/* Enhanced pointer/indicator */}
              <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -mt-6 z-20">
                <div className="relative">
                  <div className="w-6 h-8 bg-gradient-to-b from-red-500 to-red-700 rounded-b-full flex items-end justify-center shadow-xl border-2 border-white">
                    <div className="w-3 h-3 bg-white rounded-full mb-1 shadow-inner"></div>
                  </div>
                  <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2">
                    <div className="w-0 h-0 border-l-4 border-r-4 border-t-6 border-l-transparent border-r-transparent border-t-red-700"></div>
                  </div>
                  {/* Glow effect */}
                  <div className="absolute inset-0 bg-red-500 rounded-b-full blur-sm opacity-50 animate-pulse"></div>
                </div>
              </div>
            </div>

            {/* Reward Display */}
            {reward && (
              <div className="bg-gradient-to-r from-green-50 via-emerald-50 to-teal-50 border-2 border-green-200 rounded-2xl p-6 mb-6 text-center transform transition-all duration-500 shadow-xl animate-bounce">
                <div className="text-4xl mb-3 animate-pulse">🎉</div>
                <h3 className="text-2xl font-black text-green-700 mb-2">Congratulations!</h3>
                <p className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-green-600 to-emerald-600 mb-3">
                  {reward.message || `You won ${reward.coins} coins!`}
                </p>
                <div className="bg-white/50 rounded-lg p-3 mb-3">
                  <p className="text-sm text-gray-600">New Balance</p>
                  <p className="text-2xl font-black text-green-700">{reward.newBalance} coins</p>
                </div>
                {cooldown.active && cooldown.timeLeft > 0 && (
                  <div className="flex items-center justify-center text-orange-600 bg-orange-50 rounded-lg p-2">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-sm font-medium">Next spin in {formatTime(cooldown.timeLeft)}</span>
                  </div>
                )}
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border-2 border-red-200 text-red-700 p-4 rounded-xl mb-6 shadow-lg">
                <div className="flex items-center">
                  <svg className="w-5 h-5 mr-2 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="font-medium">{error}</p>
                </div>
              </div>
            )}

            {/* Enhanced Spin Button */}
            <button
              onClick={handleSpinClick}
              disabled={isSpinning || cooldown.active}
              className={`group relative px-12 py-4 rounded-full font-black text-lg shadow-2xl transition-all transform hover:scale-110 disabled:cursor-not-allowed disabled:transform-none overflow-hidden ${
                isSpinning || cooldown.active
                  ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                  : 'bg-gradient-to-r from-purple-600 via-pink-600 to-yellow-500 hover:from-purple-700 hover:via-pink-700 hover:to-yellow-600 text-white'
              }`}
            >
              {/* Button glow effect */}
              {!isSpinning && !cooldown.active && (
                <div className="absolute inset-0 bg-gradient-to-r from-purple-400 via-pink-400 to-yellow-400 rounded-full blur opacity-50 group-hover:opacity-75 transition-opacity"></div>
              )}

              <span className="relative z-10 flex items-center">
                {isSpinning ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-gray-600" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Spinning...
                  </>
                ) : cooldown.active ? (
                  <>
                    <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {formatTime(cooldown.timeLeft)}
                  </>
                ) : (
                  <>
                    <span className="mr-3">🎰</span>
                    SPIN NOW
                    <span className="ml-3 group-hover:translate-x-1 transition-transform">→</span>
                  </>
                )}
              </span>
            </button>

            {/* Instructions */}
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-500 mb-2">
                🎲 Spin the wheel to win random coin rewards
              </p>
              <p className="text-xs text-gray-400">
                Higher rewards = longer cooldowns • Use coins for tournaments & exclusive items
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Rules & Info Card */}
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl overflow-hidden border border-white/20">
        <div className="p-6 bg-gradient-to-r from-purple-50 via-pink-50 to-yellow-50 border-b border-purple-100">
          <h3 className="font-bold text-gray-800 flex items-center">
            <span className="mr-2">📋</span>
            How It Works
          </h3>
        </div>
        <div className="p-6">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <div className="flex items-start">
                <span className="text-purple-500 mr-3 mt-1">🎯</span>
                <div>
                  <p className="font-medium text-gray-800">Random Rewards</p>
                  <p className="text-sm text-gray-600">Win coins from 5 to 200 randomly</p>
                </div>
              </div>
              <div className="flex items-start">
                <span className="text-pink-500 mr-3 mt-1">⏰</span>
                <div>
                  <p className="font-medium text-gray-800">Smart Cooldowns</p>
                  <p className="text-sm text-gray-600">Bigger wins = longer wait times</p>
                </div>
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex items-start">
                <span className="text-yellow-500 mr-3 mt-1">💰</span>
                <div>
                  <p className="font-medium text-gray-800">Instant Credits</p>
                  <p className="text-sm text-gray-600">Coins added to your balance immediately</p>
                </div>
              </div>
              <div className="flex items-start">
                <span className="text-green-500 mr-3 mt-1">🏆</span>
                <div>
                  <p className="font-medium text-gray-800">Tournament Entry</p>
                  <p className="text-sm text-gray-600">Use coins for special tournaments</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpinnerReward;
